import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { getCheckouts, returnBook } from '../api/checkouts';
import NewCheckoutModal from '../components/checkouts/NewCheckoutModal';

import toast from 'react-hot-toast';

export default function CheckoutsPage() {
  const [status, setStatus] = useState('active');
  const [q, setQ] = useState('');
  const [showNew, setShowNew] = useState(false);
  const qc = useQueryClient();
  const { t } = useTranslation();

  const tabs = [
    { key:'active', label:t('active'), color:'var(--warning)' },
    { key:'overdue', label:t('overdue'), color:'var(--danger)' },
    { key:'returned', label:t('returned'), color:'var(--success)' },
  ];

  const { data, isLoading } = useQuery({
    queryKey: ['checkouts', status, q],
    queryFn: () => getCheckouts({ status, q }),
  } as any);

  const returnMutation = useMutation({
    mutationFn: (id: number) => returnBook(id),
    onSuccess: () => { toast.success(t('returned')); qc.invalidateQueries({ queryKey: ['checkouts'] }); qc.invalidateQueries({ queryKey: ['stats'] }); },
    onError: () => toast.error('Error')
  });

  const checkouts = (data as any)?.data?.checkouts || [];

  return (
    <>
      <NewCheckoutModal open={showNew} onClose={() => setShowNew(false)} />
      <div style={{padding:24,fontFamily:'system-ui,sans-serif'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
          <h1 style={{fontSize:20,fontWeight:600,color:'var(--text-primary)',margin:0}}>{t('circulation')}</h1>
          <button onClick={() => setShowNew(true)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}>
            <Plus size={15} /> ახალი გაცემა
          </button>
        </div>

        <div style={{marginBottom:16}}>
          <input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="ძიება: წიგნი, წევრი, ბარკოდი..."
            style={{width:'100%',boxSizing:'border-box',padding:'8px 14px',background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:8,color:'var(--text-secondary)',fontSize:13,outline:'none'}}
          />
        </div>
        <div style={{display:'flex',gap:4,marginBottom:16,borderBottom:'1px solid #1e2d45'}}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setStatus(tab.key)}
              style={{padding:'8px 16px',background:'none',border:'none',borderBottom:status===tab.key?`2px solid ${tab.color}`:'2px solid transparent',marginBottom:-1,fontSize:13,color:status===tab.key?tab.color:'var(--text-faint)',cursor:'pointer',fontWeight:status===tab.key?500:400,transition:'all 0.15s'}}>
              {tab.label}
            </button>
          ))}
        </div>

        <div style={{background:'var(--bg-card)',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
            <thead>
              <tr style={{borderBottom:'1px solid #1e2d45'}}>
                {[t('book'),t('member'),t('checked_out'),t('due_date'),t('status'),''].map((h,i) => (
                  <th key={i} style={{textAlign:'left',padding:'10px 16px',fontSize:11,fontWeight:500,color:'var(--text-faint)',letterSpacing:'0.03em'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading && <tr><td colSpan={6} style={{textAlign:'center',padding:32,color:'var(--text-dimmed)'}}>{t('loading')}</td></tr>}
              {checkouts.map((c: any) => {
                const isOverdue = !c.returned_at && new Date(c.due_date) < new Date();
                return (
                  <tr key={c.id} style={{borderBottom:'1px solid #0f1826'}}
                    onMouseEnter={e => (e.currentTarget.style.background='var(--bg-hover)')}
                    onMouseLeave={e => (e.currentTarget.style.background='transparent')}>
                    <td style={{padding:'10px 16px'}}>
                      <div style={{color:'var(--text-secondary)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:220}}>{c.title}</div>
                      <div style={{color:'var(--text-faint)',fontSize:11,marginTop:2}}>{c.author}</div>
                    </td>
                    <td style={{padding:'10px 16px',color:'var(--text-muted)'}}>{c.member_name}</td>
                    <td style={{padding:'10px 16px',color:'var(--text-dimmed)',fontSize:11}}>{new Date(c.checked_out_at).toLocaleDateString()}</td>
                    <td style={{padding:'10px 16px',fontSize:11,fontWeight:500,color:isOverdue?'var(--danger)':'var(--text-muted)'}}>{new Date(c.due_date).toLocaleDateString()}</td>
                    <td style={{padding:'10px 16px'}}>
                      <span style={{display:'inline-flex',padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:500,
                        background:c.returned_at?'var(--success-bg)':isOverdue?'var(--danger-bg)':'var(--warning-bg)',
                        color:c.returned_at?'var(--success)':isOverdue?'var(--danger)':'var(--warning)'}}>
                        {c.returned_at?t('returned'):isOverdue?t('overdue'):t('active')}
                      </span>
                    </td>
                    <td style={{padding:'10px 16px'}}>
                      {!c.returned_at && (
                        <button onClick={() => returnMutation.mutate(c.id)}
                          style={{background:'none',border:'1px solid #1e2d45',borderRadius:6,padding:'4px 10px',fontSize:11,color:'var(--accent)',cursor:'pointer'}}>
                          {t('return')}
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
